import { pickAffiliateId, resolveAffiliateIds } from "./affiliate";

// The authentication state exposed to the router as typed context. It is derived
// from the live OIDC session by `useAuthStoreSync` and consumed by route guards.
export interface AuthState {
  readonly isAuthenticated: boolean;
  // True while the OIDC library is still resolving the session (initial load,
  // redirect callback, silent renew). Guards must wait rather than redirect.
  readonly isLoading: boolean;
  readonly userId: string | null;
  // The effective affiliate (tenant) id: the user's stored choice when it is
  // still in the organization claim, otherwise the claim's default.
  readonly affiliateId: string | null;
  // Every affiliate the organization claim allows, in claim order. Drives the
  // affiliate switcher, which stays hidden when there is nothing to switch to.
  readonly availableAffiliateIds: readonly string[];
}

// Default state used while no identity is known. Kept as a shared constant so the
// app, tests and the auth bridge agree on the "logged out" shape. It also seeds
// the router context before the provider supplies the real state.
export const anonymousAuthState: AuthState = {
  isAuthenticated: false,
  isLoading: false,
  userId: null,
  affiliateId: null,
  availableAffiliateIds: [],
};

// The subset of react-oidc-context's auth value that `deriveAuthState` reads.
// Kept structural so the mapping can be unit-tested with plain objects.
export interface OidcAuthSnapshot {
  readonly isAuthenticated: boolean;
  readonly isLoading: boolean;
  readonly user?: {
    readonly profile?: {
      readonly sub?: string;
      readonly organization?: unknown;
    };
  } | null;
}

// Maps a raw OIDC session onto the router `AuthState`, resolving the affiliates
// from the organization claim. `selectedAffiliateId` is the user's persisted
// switcher choice; it is honoured only while the claim still contains it, so a
// revoked organization falls back to the claim default instead of pinning the
// app to a tenant every request would 403 on.
export function deriveAuthState(
  auth: OidcAuthSnapshot,
  selectedAffiliateId: string | null = null,
): AuthState {
  const availableAffiliateIds = resolveAffiliateIds(
    auth.user?.profile?.organization,
  );

  return {
    isAuthenticated: auth.isAuthenticated,
    isLoading: auth.isLoading,
    userId: auth.user?.profile?.sub ?? null,
    affiliateId: pickAffiliateId(availableAffiliateIds, selectedAffiliateId),
    availableAffiliateIds,
  };
}
